import { useEffect, useId, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type MouseEvent } from "react";
import { createPortal } from "react-dom";

type MermaidInstance = typeof import("mermaid").default;

let mermaidPromise: Promise<MermaidInstance> | undefined;

function loadMermaid() {
	if (!mermaidPromise) {
		mermaidPromise = import("mermaid").then(({ default: mermaid }) => {
			mermaid.initialize({
				startOnLoad: false,
				securityLevel: "strict",
				theme: "base",
				fontFamily: "Plain, Arial, sans-serif",
				themeVariables: {
					primaryColor: "#f6efe7",
					primaryTextColor: "#191816",
					primaryBorderColor: "#776f65",
					lineColor: "#555049",
					secondaryColor: "#e9f0f3",
					tertiaryColor: "#fff5e8",
				},
			});
			return mermaid;
		});
	}
	return mermaidPromise;
}

export function MermaidDiagram({ chart }: { chart: string }) {
	const containerRef = useRef<HTMLDivElement>(null);
	const closeButtonRef = useRef<HTMLButtonElement>(null);
	const reactId = useId();
	const [error, setError] = useState<Error | null>(null);
	const [rendered, setRendered] = useState(false);
	const [svgMarkup, setSvgMarkup] = useState("");
	const [viewerOpen, setViewerOpen] = useState(false);

	useEffect(() => {
		let cancelled = false;
		const container = containerRef.current;
		if (!container) return;

		const diagramId = `qblog-mermaid-${reactId.replace(/[^a-zA-Z0-9_-]/g, "")}`;
		container.replaceChildren();
		setError(null);
		setRendered(false);
		setSvgMarkup("");
		setViewerOpen(false);

		loadMermaid()
			.then((mermaid) => mermaid.render(diagramId, chart))
			.then(({ svg, bindFunctions }) => {
				if (cancelled) return;
				container.innerHTML = svg;
				bindFunctions?.(container);
				setSvgMarkup(svg);
				setRendered(true);
			})
			.catch((reason: unknown) => {
				if (cancelled) return;
				container.replaceChildren();
				setError(reason instanceof Error ? reason : new Error("Mermaid 图表渲染失败"));
			});

		return () => {
			cancelled = true;
			container.replaceChildren();
		};
	}, [chart, reactId]);

	useEffect(() => {
		if (!viewerOpen) return;
		const previousFocus = document.activeElement;
		const previousOverflow = document.body.style.overflow;
		closeButtonRef.current?.focus();
		document.body.style.overflow = "hidden";
		function closeOnEscape(event: globalThis.KeyboardEvent) {
			if (event.key !== "Escape") return;
			event.preventDefault();
			event.stopPropagation();
			setViewerOpen(false);
		}
		document.addEventListener("keydown", closeOnEscape, true);
		return () => {
			document.removeEventListener("keydown", closeOnEscape, true);
			document.body.style.overflow = previousOverflow;
			if (previousFocus instanceof HTMLElement) previousFocus.focus({ preventScroll: true });
		};
	}, [viewerOpen]);

	function openViewer(event: MouseEvent<HTMLDivElement>) {
		event.stopPropagation();
		if (rendered && svgMarkup) setViewerOpen(true);
	}

	function openViewerFromKeyboard(event: ReactKeyboardEvent<HTMLDivElement>) {
		if (event.key !== "Enter" && event.key !== " ") return;
		event.preventDefault();
		event.stopPropagation();
		if (rendered && svgMarkup) setViewerOpen(true);
	}

	if (error) {
		return <pre className="mermaid-fallback" title={error.message}><code className="language-mermaid">{chart}</code></pre>;
	}

	const zoomable = rendered && !!svgMarkup;
	return <>
		<div ref={containerRef} className="mermaid-diagram" data-zoomable={zoomable ? "true" : undefined} role={zoomable ? "button" : "img"} tabIndex={zoomable ? 0 : undefined} aria-label={zoomable ? "点击放大 Mermaid 架构图" : "Mermaid 架构图"} aria-busy={!rendered} onClickCapture={openViewer} onKeyDown={openViewerFromKeyboard} />
		{viewerOpen && svgMarkup && createPortal(
			<div className="mermaid-viewer-backdrop" role="presentation" onMouseDown={() => setViewerOpen(false)}>
				<div className="mermaid-viewer" role="dialog" aria-modal="true" aria-label="放大查看 Mermaid 架构图" onMouseDown={(event) => event.stopPropagation()}>
					<button ref={closeButtonRef} className="mermaid-viewer-close" type="button" aria-label="关闭图表" onClick={() => setViewerOpen(false)}>×</button>
					<div className="mermaid-viewer-canvas" dangerouslySetInnerHTML={{ __html: svgMarkup }} />
				</div>
			</div>,
			document.body,
		)}
	</>;
}
